/**
 * ApplicationRoutes
 * @description Holds all application routes
 */
export const ApplicationRoutes = {
  // Dashboard
  AdminDashboard: '/user/admin-dashboard',
  // Profile
  UserProfile: '/user/profile',
  EditUserProfile: '/user/edit-profile',
  // Case
  CaseAdd: '/user/case/add',
  CaseView: '/user/case/view',
  CaseEdit: '/user/case/edit',
  CaseDraft: '/user/case/draft',
  CaseCreated: '/user/case/created',
  CaseSubmitted: '/user/case/submitted',
  CaseCompleted: '/user/case/completed',
  // Staff User
  InviteUser: '/user/inviteuser',
  UserList: '/user/userslist',
  // Patients
  PatientList: '/user/patientslist',
  AddPatient: '/user/addpatients',
  EditPatient: '/user/editpatients',
  PatientProfile: '/user/patientsprofile',
  PatientSettings: '/user/patient-settings',
  // Invoice
  InvoiceList: '/user/invoicelist',
  // Branch
  BranchList: '/user/branchlist',
  SelectBranchLocation: '/select-branch-location',
  // Settings
  UserSettings: '/user/settings',
  // Auth
  Signup: 'signup',
  Login: '/',
};
export const UserModules = {
  CASE: 'CASE',
  REPORT: 'REPORT',
  ACCOUNT: 'ACCOUNT',
};
const UserType = {
  USER: 'LOCATION_USER',
  ADMIN: 'LOCATION_ADMIN',
};

/**
 * GeneralUserRoutes
 * @description Holds routes for `USER`
 */
const GeneralUserRoutes = [
  ApplicationRoutes.AdminDashboard,
  ApplicationRoutes.InviteUser,
  ApplicationRoutes.AddPatient,
  ApplicationRoutes.EditPatient,
  ApplicationRoutes.PatientProfile,
  ApplicationRoutes.PatientSettings,
  ApplicationRoutes.PatientList,
  ApplicationRoutes.UserProfile,
  ApplicationRoutes.EditUserProfile
];
/**
 * AdminUserRoutes
 * @description Holds routes for `ADMIN`
 */
const AdminUserRoutes = [...GeneralUserRoutes, ApplicationRoutes.BranchList, ApplicationRoutes.UserList];
/**
 * CaseModuleRoutes
 */
const CaseModuleRoutes = [
  ApplicationRoutes.CaseAdd,
  ApplicationRoutes.CaseView,
  ApplicationRoutes.CaseEdit,
  ApplicationRoutes.CaseDraft,
  ApplicationRoutes.CaseCreated,
  ApplicationRoutes.CaseSubmitted,
  ApplicationRoutes.CaseCompleted,
];
/**
 * ReportModuleRoutes
 */
const ReportModuleRoutes = [];
/**
 * AccountModuleRoutes
 */
const AccountModuleRoutes = [
  ApplicationRoutes.InvoiceList,
  ApplicationRoutes.UserSettings,
];

/**
 * modulesHierarchy
 */
const ModulesHierarchy = {
  [UserModules.CASE]: CaseModuleRoutes,
  [UserModules.REPORT]: ReportModuleRoutes,
  [UserModules.ACCOUNT]: AccountModuleRoutes,
};

/**
 * UserTypeHierarchy
 */
const UserTypeHierarchy = {
  [UserType.USER]: GeneralUserRoutes,
  [UserType.ADMIN]: AdminUserRoutes,
};

/**
 * userRoutesByActiveModules
 * @param activeModules Active user modules list
 * @returns User's accessible routes for by given active modules
 */
const userRoutesByActiveModules = (activeModules) => {
  return activeModules?.flatMap((item) => ModulesHierarchy[item]) || [];
};

/**
 * userRoutesByUserType
 * @param type User type derived from the `isClinicLocationAdmin` of the selected user location
 * @returns User's accessible routes for given user type
 */
const userRoutesByUserType = (userType) => {
  return UserTypeHierarchy[userType || ''] || [];
};

/**
 * getDisabledRoutes
 * @returns List of disabled routes in the application
 */
const getDisabledRoutes = () => {
  // Add any routes to be disabled on live
  return [];
};

/**
 * getUserApplicationRoutes
 * @param selectedLocation User selected branch location, it holds data like `isClinicLocationAdmin` & `modules`
 * @returns Active application routes for the user
 */
export const getUserApplicationRoutes = (selectedLocation) => {
  const userRoutesByModules = userRoutesByActiveModules(
    selectedLocation?.modules
  );
  const userRoutesByType = userRoutesByUserType(
    selectedLocation?.isClinicLocationAdmin ? UserType.ADMIN : UserType.USER
  );

  return (
    userRoutesByType
      // Add user's active module routes
      .concat(userRoutesByModules)
      // Filter disabled application routes
      .filter((route) => !getDisabledRoutes().includes(route))
  );
};
/**
 * hasModuleAccess
 * @param module Module name
 * @param activeModules Active user modules list
 * @returns Boolean value to check if the user has access to the given module
 */
export const hasModuleAccess = (module, activeModules) => {
  return activeModules?.includes(module) || false;
};
